import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Eye, EyeOff, Save, UserRound } from "lucide-react";
import Button from "../components/Button";
import Spinner from "../components/Spinner";
import { profileAPI } from "../services/api";
import { useToasts } from "../hooks/useToasts";
import "./PublicProfile.css";

const PUBLIC_BIO_MAX_LENGTH = 1000;

const PublicProfile = () => {
  const navigate = useNavigate();
  const { addToast } = useToasts();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [profile, setProfile] = useState(null);
  const [formData, setFormData] = useState({
    publicDisplayName: "",
    publicBio: "",
    publicInterests: "",
    publicProfileVisible: false,
  });

  useEffect(() => {
    const loadPublicProfile = async () => {
      try {
        const response = await profileAPI.getPublicProfile();
        const publicProfile = response.data.publicProfile;
        setProfile(publicProfile);
        setFormData({
          publicDisplayName: publicProfile.publicDisplayName || "",
          publicBio: publicProfile.publicBio || "",
          publicInterests: publicProfile.publicInterests || "",
          publicProfileVisible: publicProfile.publicProfileVisible === true,
        });
      } catch (error) {
        addToast(
          error.response?.data?.details?.[0] || "Failed to load public profile",
          "error-warning",
        );
      } finally {
        setLoading(false);
      }
    };

    loadPublicProfile();
  }, [addToast]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleVisibilityToggle = () => {
    setFormData((prev) => ({
      ...prev,
      publicProfileVisible: !prev.publicProfileVisible,
    }));
  };

  const handleSave = async (event) => {
    event.preventDefault();

    if (!formData.publicDisplayName.trim()) {
      addToast("Public name is required", "error-warning");
      return;
    }

    try {
      setSaving(true);
      const response = await profileAPI.updatePublicProfile({
        publicDisplayName: formData.publicDisplayName.trim(),
        publicBio: formData.publicBio.trim(),
        publicInterests: formData.publicInterests.trim(),
        publicProfileVisible: formData.publicProfileVisible,
      });
      setProfile(response.data.publicProfile);
      addToast("Public profile updated", "success-info");
    } catch (error) {
      addToast(
        error.response?.data?.details?.[0] || "Failed to update public profile",
        "error-warning",
      );
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Spinner visible={true} />;

  return (
    <main className="public-profile-page">
      <div className="public-profile-header">
        <div>
          <h1>Public Profile</h1>
          <p>How members see you in Asasiasa communities.</p>
        </div>
        <Button
          className="button-btn button-btn-blue public-profile-view-button"
          onClick={() => navigate(`/users/${profile?.userId}`)}
          disabled={!profile?.userId}
        >
          <UserRound size={16} />
          View
        </Button>
      </div>

      <div className="public-profile-layout">
        <form className="public-profile-form" onSubmit={handleSave}>
          <section className="public-profile-section">
            <div className="public-profile-field">
              <label htmlFor="publicDisplayName">Public name</label>
              <input
                id="publicDisplayName"
                name="publicDisplayName"
                type="text"
                value={formData.publicDisplayName}
                onChange={handleChange}
                maxLength={50}
                placeholder="Your public Asasiasa name"
              />
            </div>
            <div className="public-profile-field">
              <label htmlFor="publicBio">Bio</label>
              <textarea
                id="publicBio"
                name="publicBio"
                value={formData.publicBio}
                onChange={handleChange}
                rows="4"
                maxLength={PUBLIC_BIO_MAX_LENGTH}
                placeholder="A short note about you."
              />
              <span className="public-profile-counter">
                {formData.publicBio.length}/{PUBLIC_BIO_MAX_LENGTH}
              </span>
            </div>
            <div className="public-profile-field">
              <label htmlFor="publicInterests">Interests</label>
              <input
                id="publicInterests"
                name="publicInterests"
                type="text"
                value={formData.publicInterests}
                onChange={handleChange}
                maxLength={120}
                placeholder="History, languages, medicine..."
              />
            </div>
            <div className="public-profile-field">
              <label htmlFor="publicProfileVisible">Visibility</label>
              <button
                id="publicProfileVisible"
                type="button"
                className={
                  formData.publicProfileVisible
                    ? "public-profile-visibility-toggle is-visible"
                    : "public-profile-visibility-toggle"
                }
                onClick={handleVisibilityToggle}
              >
                {formData.publicProfileVisible ? (
                  <Eye size={18} />
                ) : (
                  <EyeOff size={18} />
                )}
                <span>
                  {formData.publicProfileVisible
                    ? "Visible in public profile and Meet People"
                    : "Profile details hidden"}
                </span>
              </button>
              <p className="public-profile-help">
                Your public name can still appear in communities, but bio and
                interests stay hidden from your public profile and Meet People
                when visibility is off.
              </p>
            </div>
          </section>

          <div className="public-profile-actions">
            <Button
              type="submit"
              className="button-btn button-btn-blue public-profile-save-button"
              disabled={saving}
            >
              <Save size={16} />
              {saving ? "Saving..." : "Save"}
            </Button>
          </div>
        </form>

        <aside className="public-profile-preview">
          <div className="public-profile-preview-avatar">
            {(formData.publicDisplayName || "M").trim().charAt(0).toUpperCase()}
          </div>
          <h2>{formData.publicDisplayName || "Asasiasa member"}</h2>
          <p>
            {formData.publicProfileVisible
              ? formData.publicBio || "No public bio yet."
              : "Profile details are hidden."}
          </p>
          {formData.publicProfileVisible && formData.publicInterests ? (
            <div className="public-profile-preview-interests">
              {formData.publicInterests}
            </div>
          ) : null}
        </aside>
      </div>
    </main>
  );
};

export default PublicProfile;
